import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useLayoutEffect,
} from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Sidebar from "../Sidebar/sidebar";
import { updateProgress } from "../../utils/updateProgress";
import UserProgressContext from "../../userProgressContext";
import isPositiveInteger from "../../utils/isPositiveInteger";
import { totalLevel } from "../../constants/constants";
import Navbar from "../Navbar/navbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleArrowLeft,
  faCircleArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import SoundManager from "../../services/SoundManager";
import { saveUserProgress } from "../../services/saveUserProgress";
const GameLevelLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location && location.state;
  const pathname = location && location.pathname;
  const { levelId: currLevel } = useParams();

  const {
    audioMuted,
    setAudioSrc,
    name,
    emailId,
    isUserProgressFetched,
    progress,
    setProgress,
    localProgress,
    setLocalProgress,
    isLevelEnd,
    setIsLevelEnd,
    totalScore,
    setTotalScore,
    redeemedScores,
    setRedeemedScores,
    finishedTasks,
    nextButtonLocked,
    completedMinigames,
  } = useContext(UserProgressContext);

  const [backButtonStyle, setBackButtonStyle] = useState({});
  const [nextButtonStyle, setNextButtonStyle] = useState({});
  const [script, setScript] = useState();
  const isFirstEffectReturnedEarly = useRef(false);

  const hasInvokedSaveUserProgress = useRef(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const previousLocationPathname = state && state.previousLocationPathname;

  useLayoutEffect(() => {
    const updateButtonPositions = () => {
      const mainDiv = document.getElementById("main");
      if (mainDiv) {
        const mainRect = mainDiv.getBoundingClientRect();
        const backButtonSubtraction = window.innerWidth >= 1024 ? 20 : 25;
        const backButtonLeft = mainRect.left - backButtonSubtraction;
        const backButtonTop = mainRect.top + mainRect.height / 2;
        const nextButtonSubtraction = window.innerWidth >= 1024 ? 25 : 40;
        const nextButtonRight =
          window.innerWidth - mainRect.right - nextButtonSubtraction;
        const nextButtonTop = mainRect.top + mainRect.height / 2;

        setBackButtonStyle({
          left: `${backButtonLeft}px`,
          top: `${backButtonTop}px`,
        });

        setNextButtonStyle({
          right: `${nextButtonRight}px`,
          top: `${nextButtonTop}px`,
        });
      }
    };

    window.addEventListener("resize", updateButtonPositions);
    window.addEventListener("scroll", updateButtonPositions);
    updateButtonPositions();

    return () => {
      window.removeEventListener("resize", updateButtonPositions);
      window.removeEventListener("scroll", updateButtonPositions);
    };
  }, []);

  useEffect(() => {
    if (isUserProgressFetched && currLevel) {
      if (
        !isPositiveInteger(currLevel) ||
        !isPositiveInteger(progress) ||
        parseInt(currLevel) > totalLevel ||
        parseInt(currLevel) > progress
      ) {
        // console.log(
        //   "inside gameLevelLayout useEffect 0.0:",
        //   isUserProgressFetched,
        //   localProgress,
        //   progress,
        //   currLevel
        // );
        isFirstEffectReturnedEarly.current = true;
        return navigate("/page-not-found");
      }
      if (
        previousLocationPathname &&
        (previousLocationPathname.includes("Arsenal") ||
          previousLocationPathname.includes("securityAlert") ||
          previousLocationPathname.includes("Challenge"))
      ) {
        // console.log("inside gameLevelLayout useEffect 0.1:", localProgress);
        setLocalProgress(localProgress);
      } else {
        // console.log("inside gameLevelLayout useEffect 0.2:");
        setLocalProgress(parseInt(currLevel) + 0.01);
      }
    }
  }, [currLevel, isUserProgressFetched]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isFirstEffectReturnedEarly.current) {
      // console.log("inside gameLevelLayout useEffect 1:");
      setIsLevelEnd(false);
      const handleProgressUpdate = () => {
        if (emailId && name) {
          saveUserProgress({
            name: name,
            emailId: emailId,
            progress: progress,
            redeemedScores: redeemedScores,
            finishedTasks: finishedTasks,
          });
          hasInvokedSaveUserProgress.current = true;
        }
      };
      // Cancel the timeout if the component unmounts before it fires (e.g. level
      // navigation) so we don't get a duplicate POST from the old component after
      // the new level's component has already fired its own save.
      let timeoutId;
      try {
        timeoutId = setTimeout(() => {
          if (!hasInvokedSaveUserProgress.current) handleProgressUpdate();
        }, 200);
      } catch (error) {
        console.error("Error updating user progress:", error);
      }
      // Do not reset localProgress when returning from a crossover route (Arsenal /
      // securityAlert / Challenge) — the first useEffect already preserved it.
      const isCrossoverReturn =
        previousLocationPathname &&
        (previousLocationPathname.includes("Arsenal") ||
          previousLocationPathname.includes("securityAlert") ||
          previousLocationPathname.includes("Challenge"));
      if (currLevel && !isCrossoverReturn) {
        // console.log("inside gameLevelLayout useEffect 1.1:");
        setLocalProgress(parseInt(currLevel) + 0.01);
      }
      return () => clearTimeout(timeoutId);
    }
  }, [isUserProgressFetched, progress]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isFirstEffectReturnedEarly.current) {
      // console.log(
      //   "inside gameLevelLayout useEffect 2:",
      //   previousLocationPathname,
      //   pathname
      // );
      if (
        (localProgress &&
          (parseFloat(localProgress) % 1).toFixed(2).toString() === "0.01") ||
        (previousLocationPathname &&
          (previousLocationPathname.includes("Arsenal") ||
            previousLocationPathname.includes("securityAlert") ||
            previousLocationPathname.includes("Challenge"))) ||
        (pathname && pathname.includes("Arsenal")) ||
        pathname.includes("Alert") ||
        pathname.includes("Challenge")
      ) {
        // console.log(
        //   "inside gameLevel useEffect 2.1:",
        //   localProgress,
        //   isLevelEnd
        // );
        handleScriptImport();
      }

      // Note: no immediate navigate-back from /securityAlert here.
      // The loop is already prevented because script === null on mount, so the
      // crossover check in useEffect([localProgress]) below is skipped. The
      // SecurityAlert page renders its content; when the user clicks Next,
      // localProgress advances to the next step whose crossover ("/level/2")
      // navigates back with proper crossover-return state.
    }
  }, [localProgress]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isFirstEffectReturnedEarly.current) {
      // handle bgSound update
      if (
        localProgress &&
        script &&
        script[localProgress.toString()] &&
        "audioSrc" in script[localProgress.toString()]
      )
        setAudioSrc(script[localProgress.toString()]["audioSrc"]);
    }
  }, [localProgress, script]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isFirstEffectReturnedEarly.current) {
      // console.log("inside gameLevelLayout useEffect3.0:");
      if (localProgress && script) {
        if (script[localProgress.toString()]) {
          // handle crossovers
          if ("crossover" in script[localProgress.toString()]) {
            const targetRoute = script[localProgress.toString()]["crossover"];
            setIsTransitioning(true);
            const tid = setTimeout(() => {
              navigate(targetRoute, {
                state: {
                  previousLocationPathname: pathname,
                  crossover: true,
                },
              });
            }, 200);
            return () => clearTimeout(tid);
          }

          // handle score update — skip MCQ steps; they award on correct answer via gameLevel
          if (
            "score" in script[localProgress.toString()] &&
            !("question" in script[localProgress.toString()]) &&
            !redeemedScores.has(localProgress.toString())
          ) {
            handleScoreUpdate(script[localProgress.toString()]["score"]);
          }
        }

        const scriptKeys = Object.keys(script)[0];

        //   handle levelEnd logic
        if (
          Math.trunc(localProgress).toString() ===
            Math.trunc(scriptKeys).toString() &&
          script[localProgress.toString()] === undefined
        ) {
          // console.log(
          //   "inside gameLevelLayout useEffect3.1:",
          //   localProgress,
          //   script
          // );
          setIsLevelEnd(true);
        } else {
          setIsLevelEnd(false);
        }
      }
    }
  }, [localProgress]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // currLevel is undefined on crossover routes (/securityAlert, etc.) that
    // sit at the END of a level. Fall back to Math.trunc(localProgress) so
    // the level-end auto-navigate fires correctly from those routes too.
    const effectiveLevel =
      currLevel !== undefined
        ? parseInt(currLevel)
        : Math.trunc(parseFloat(localProgress));
    if (localProgress && localProgress > effectiveLevel + 0.01 && isLevelEnd) {
      // console.log(
      //   "inside gameLevelLayout useEffect4",
      //   localProgress,
      //   isLevelEnd
      // );
      onNextClick();
    }
  }, [isLevelEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  // console.log("inside gameLevelLayout", previousLocationPathname, location);

  const handleScriptImport = async () => {
    try {
      const nextLevelNum = Math.trunc(parseFloat(localProgress));
      // Only crossfade when the level number genuinely changes (not on crossover route reloads)
      const currentLevelNum = script
        ? Math.trunc(parseFloat(Object.keys(script)[0]))
        : null;
      const isLevelChange = currentLevelNum !== nextLevelNum;
      // Fade out first, wait for it to complete, then swap content, then fade in.
      // This prevents new content from appearing while still visible mid-transition.
      if (isLevelChange) {
        setIsTransitioning(true);
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
      const importedScript = await import(`../../scripts/level${nextLevelNum}.json`);
      setScript(importedScript);
      if (isLevelChange) setTimeout(() => setIsTransitioning(false), 30);
      else setIsTransitioning(false);
    } catch (error) {
      console.error("Error importing JSON:", error);
    }
  };

  const handleScoreUpdate = (newScore) => {
    const newRedeemedScoresSet = new Set(
      redeemedScores.add(localProgress.toString())
    );
    saveUserProgress({
      name: name,
      emailId: emailId,
      totalScore: parseInt(totalScore) + newScore,
      redeemedScores: newRedeemedScoresSet,
      finishedTasks: finishedTasks,
    });
    setTotalScore(parseInt(totalScore) + newScore);
    setRedeemedScores(newRedeemedScoresSet);
  };

  const onNextClick = () => {
    // isLevelEndRef.current = checkForLevelEnd();
    // console.log("inside handleNextEvent", isLevelEnd, localProgress);
    SoundManager.playSfx('game-retro-click');
    handleNavigation(1);
  };
  const onBackClick = () => {
    // console.log(
    //   "onBackClick",
    //   localProgress.toString() === (parseInt(currLevel) + 0.01).toString()
    // );
    SoundManager.playSfx('game-retro-click');
    if (localProgress.toString() === (parseInt(currLevel) + 0.01).toString()) {
      // console.log("inside if");
      const nextLevel = parseInt(currLevel) - 1;
      if (nextLevel >= 0) {
        navigate("/level/" + (parseInt(currLevel) - 1));
        setLocalProgress(((localProgress * 10 - 1 * 10) / 10).toFixed(2));
      }
    } else {
      // console.log("inside else");
      setLocalProgress(((localProgress * 10 - 0.01 * 10) / 10).toFixed(2));
    }
  };

  const handleNavigation = (moveDirection) => {
    if (isLevelEnd) {
      // Same fallback as useEffect4: currLevel is undefined on crossover routes
      // that terminate a level (e.g. /securityAlert at the end of level 4).
      const effectiveLevel =
        currLevel !== undefined
          ? parseInt(currLevel)
          : Math.trunc(parseFloat(localProgress));
      const nextLevel = effectiveLevel + moveDirection;
      if (Math.trunc(localProgress) + 1 > progress) {
        // console.log("inside handleNavigation if");
        hasInvokedSaveUserProgress.current = false;
        updateProgress(setProgress);
      } else {
        // console.log("inside handleNavigation else");
        setLocalProgress(
          ((nextLevel * 10 + (moveDirection / 100) * 10) / 10).toFixed(2)
        );
      }

      if (nextLevel <= totalLevel)
        navigate("/level/" + nextLevel);
      else
        navigate("/gameover", { state: { previousLocationPathname: pathname } });
    } else {
      // console.log("inside handleNextEvent else");
      setLocalProgress(
        ((localProgress * 10 + (moveDirection / 100) * 10) / 10).toFixed(2)
      );
    }
  };

  // console.log("localProgress@layout", progress, localProgress, currLevel);

  // Block the forward button when the current step is an incomplete minigame.
  // Allow skipping if the player has already finished this step before.
  const isMgBlocked =
    script != null &&
    localProgress != null &&
    script[localProgress.toString()]?.minigame != null &&
    !completedMinigames.has(localProgress.toString());

  return (
    // <div className="relative min-h-screen bg-[url('./static/images/ColonyNight.png')] bg-center bg-cover flex items-center font-sans">
    //   <div className="-z-1 absolute w-full h-full bg-white/40"></div>
    <div className="relative min-h-screen w-full flex flex-col items-center gap-1">
      <div className="w-full">
        <Navbar />
      </div>
      <div className="relative w-full h-full flex justify-center min-[1024px]:justify-normal gap-0 min-[1024px]:gap-8  min-[1280px]:gap-16">
        <div
          className={`relative hidden min-[1024px]:flex h-[97.5%] ml-2 ${
            pathname &&
            !pathname.includes("Arsenal") &&
            !pathname.includes("cyberChallenge")
              ? "visible"
              : "invisible"
          }`}
        >
          <Sidebar initialGameProgressToggle={true} />
        </div>
        <div
          id="main"
          className="relative flex gap-2 items-center w-[85%] min-[1024px]:w-9/12 h-full px-4 md:px-8"
        >
          <motion.div
            animate={{ opacity: isTransitioning ? 0 : 1 }}
            transition={{ duration: 0.3 }}
            className="relative z-10 bg-cyber-bg/80 flex flex-col gap-3 justify-center items-center p-2 min-[1024px]:p-12 text-cyber-text rounded-lg w-full h-full border border-cyber-accent/60"
          >
            {React.cloneElement(children, {
              script: script,
            })}
          </motion.div>

          <button
            id="btnBack"
            onClick={onBackClick}
            className={`${
              currLevel === "0"
                ? "disabled opacity-50 cursor-not-allowed"
                : "hover:cursor-pointer hover:text-cyber-accent transition-all ease-in delay-100"
            } ${
              (pathname && pathname.includes("level")) ||
              (state && state.crossover)
                ? "visible"
                : "invisible"
            } fixed text-white rounded-lg text-4xl md:text-5xl z-20 top-1/2 transform -translate-y-1/2`}
            style={{
              left: backButtonStyle.left,
            }}
          >
            <FontAwesomeIcon icon={faCircleArrowLeft} />
          </button>
          <button
            id="btnNext"
            onClick={onNextClick}
            disabled={nextButtonLocked || isMgBlocked}
            className={`${
              currLevel === totalLevel || nextButtonLocked || isMgBlocked
                ? "disabled opacity-50 cursor-not-allowed"
                : "hover:cursor-pointer hover:text-cyber-accent transition-all ease-in delay-100"
            } ${
              (pathname && pathname.includes("level")) ||
              (state && state.crossover)
                ? "visible"
                : "invisible"
            } fixed text-white rounded-lg text-4xl md:text-5xl z-20 top-1/2 transform -translate-y-1/2`}
            style={{
              right: nextButtonStyle.right,
            }}
          >
            <FontAwesomeIcon icon={faCircleArrowRight} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameLevelLayout;
